import {useRef, useState} from 'react';
import {Button, ButtonProps} from '@/components/app/button';
import {cn} from '@/lib/utils';
import {Form} from '../../../app/components/Form';
import {Timer} from '../../../app/components/Timer';
const fieldNames = ['digit1', 'digit2', 'digit3', 'digit4', 'digit5', 'digit6'] as const;
export interface OTPFormProps {
  cancel: () => void;
  cancelText?: string;
  className?: string;
  resend: () => Promise<unknown>;
  resendButton?: ButtonProps;
  submitButton?: ButtonProps;
  verify: (otp: string) => Promise<unknown>;
}
export const OTPForm = ({cancel, cancelText, className, resend, resendButton, submitButton, verify}: OTPFormProps) => {
  const timer = useRef<Timer>(null);
  const [expired, setExpired] = useState(false);
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [resending, setResending] = useState(false);
  const fallbackToOnChange = useRef(false);
  return (
    <Form
      className={className}
      fields={({setValue, submit}) => {
        return fieldNames.map((fieldName, index, array) => {
          const nextFieldIndex = index + 1;
          const nextFieldName = array[nextFieldIndex];
          const prevFieldIndex = index - 1;
          const prevFieldName = array[prevFieldIndex];
          return {
            className: 'min-w-0',
            label: `Digit ${index + 1}`,
            labelClassName: 'sr-only',
            name: fieldName,
            type: 'text',
            typeOptions: {
              autoFocus: index === 0 ? true : undefined,
              className: 'h-[3rem] w-auto min-w-0 p-0 text-center no-spinner appearance-none',
              disabled: expired,
              onChange: (e) => {
                if (!fallbackToOnChange.current) {
                  return;
                }
                if (e.target.value === '') {
                  if (prevFieldName != null) {
                    setTimeout(() => inputRefs.current[prevFieldIndex]?.focus());
                  }
                  return;
                }
                const value = e.target.value;
                if (/\d/.test(value)) {
                  setValue(
                    fieldName,
                    value
                      .split('')
                      .reverse()
                      .find((c) => /\d/.test(c))!,
                  );
                  if (nextFieldName != null) {
                    setTimeout(() => inputRefs.current[nextFieldIndex]?.focus());
                  } else {
                    inputRefs.current[index]?.closest('form')?.querySelector('button')?.focus();
                    submit();
                  }
                } else {
                  setValue(fieldName, '');
                }
              },
              onKeyDown: (e) => {
                if (e.key == null) {
                  fallbackToOnChange.current = true;
                  return;
                }
                if (e.key === 'Backspace') {
                  setValue(fieldName, '');
                  if (prevFieldName != null) {
                    setTimeout(() => inputRefs.current[prevFieldIndex]?.focus());
                  }
                }
                if (/\d/.test(e.key)) {
                  setValue(fieldName, e.key);
                  if (nextFieldName != null) {
                    setTimeout(() => inputRefs.current[nextFieldIndex]?.focus());
                  } else {
                    inputRefs.current[index]?.closest('form')?.querySelector('button')?.focus();
                    submit();
                  }
                }
              },
              onPaste: (e) => {
                const value = e.clipboardData.getData('text/plain');
                if (/\d{6}/.test(value)) {
                  const digits = value.split('');
                  digits.forEach((digit, index) => {
                    setValue(fieldNames[index], digit);
                  });
                  submit();
                  inputRefs.current[index]?.closest('form')?.querySelector('button')?.focus();
                }
              },
              ref: (e: HTMLInputElement) => {
                inputRefs.current[index] = e;
              },
              type: 'number',
            },
          };
        });
      }}
      id={'otp-form'}
      onSubmit={(fieldValues) => {
        const otp = fieldNames.map((fieldName) => fieldValues[fieldName]).join('');
        if (!/\d{6}/.test(otp)) {
          return Promise.reject(new Error('Invalid Code'));
        }
        return verify(otp);
      }}
      render={({renderError, renderFieldByName, renderSubmitButton}) => {
        return (
          <div>
            {renderError()}
            <div className="flex flex-row justify-center gap-4 my-4 mx-auto">
              {renderFieldByName('digit1')}
              {renderFieldByName('digit2')}
              {renderFieldByName('digit3')}
              {renderFieldByName('digit4')}
              {renderFieldByName('digit5')}
              {renderFieldByName('digit6')}
            </div>
            {!expired && renderSubmitButton()}
            {expired && (
              <Button
                className={cn('w-full h-[3rem]', 'rounded-full')}
                isLoading={resending}
                {...resendButton}
                onClick={() => onResend()}
                type={'button'}>
                {'Resend Code'}
              </Button>
            )}
            <Timer
              duration={3 * 60 * 1000}
              interval={1000}
              onEnd={() => setExpired(true)}
              render={({duration, remainingTime}) => {
                const remainingTimeInSeconds = Math.floor(remainingTime / 1000);
                const minutes = Math.floor(remainingTimeInSeconds / 60);
                const seconds = remainingTimeInSeconds % 60;
                return (
                  <div className="relative mx-4 py-4">
                    <div
                      className="relative overflow-hidden rounded-full"
                      style={{
                        height: 8,
                      }}>
                      <div className="absolute inset-0 bg-gray-300" />
                      <div
                        className={cn('absolute inset-0', remainingTimeInSeconds < 10 ? 'bg-red-400' : 'bg-indigo-400')}
                        style={{
                          borderRadius: 'inherit',
                          transform: `translateX(${100 - (remainingTime / duration) * 100}%)`,
                          transition: 'transform linear 1s',
                        }}
                      />
                    </div>
                    <div className="text-center mt-1">
                      <span className="text-sm text-muted-foreground">
                        Remaining Time {`${minutes}`.padStart(2, '0')}:{`${seconds}`.padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                );
              }}
              ref={timer}
            />
            <div className={'mt-4'}>
              <hr />
              <div className={'text-center mt-4'}>
                <span className={'text-muted-foreground'}>Didn't get the code?</span>{' '}
                <Button
                  className={'bg-transparent opacity-1 p-0'}
                  disabled={resending}
                  onClick={() => onResend()}
                  type={'button'}
                  variant="link">
                  {'Resend Code'}
                </Button>
                <span className={'text-muted-foreground'}>{' or '}</span>
                <Button
                  className={'bg-transparent opacity-1 p-0'}
                  onClick={() => cancel()}
                  type={'button'}
                  variant="link">
                  {cancelText || 'Cancel'}
                </Button>
              </div>
            </div>
          </div>
        );
      }}
      submitButton={{
        className: 'w-full h-[3rem]',
        children: 'Confirm Code',
        ...submitButton,
        disabled: expired,
      }}
    />
  );
  async function onResend() {
    setResending(true);
    try {
      await resend();
      setExpired(false);
      timer.current?.restart();
    } catch (e) {
      console.error(e);
    } finally {
      setResending(false);
    }
  }
};
